import React from 'react';
import Modal from './Modal';
import { CreditCard, FileText, Receipt, Zap } from 'lucide-react';

interface PaymentMethodDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (method: 'stripe_invoice' | 'checkout' | 'saved' | 'manual') => void;
  hasDefaultPaymentMethod?: boolean;
  canUseStripeInvoice?: boolean;
  stripeInvoiceBlockedReason?: string;
}

export const PaymentMethodDialog: React.FC<PaymentMethodDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  hasDefaultPaymentMethod = false,
  canUseStripeInvoice = true,
  stripeInvoiceBlockedReason
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose Payment Method">
      <div className="p-6 space-y-4">
        {canUseStripeInvoice ? (
          <button
            onClick={() => onSelect('stripe_invoice')}
            className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-indigo-500
                       hover:bg-indigo-50 text-left flex items-start gap-3 transition-colors"
          >
            <Receipt className="text-indigo-600 mt-1 flex-shrink-0" size={20} />
            <div>
              <h3 className="font-medium text-slate-900">Stripe Hosted Invoice</h3>
              <p className="text-sm text-slate-600 mt-1">
                Create a Stripe invoice link. Driver pays on Stripe's hosted invoice page.
              </p>
              <span className="inline-block mt-2 text-xs text-indigo-700 bg-indigo-100 px-2 py-1 rounded">
                Recommended
              </span>
            </div>
          </button>
        ) : (
          <div className="w-full p-4 border-2 border-slate-200 rounded-lg text-left flex items-start gap-3 bg-slate-50">
            <Receipt className="text-slate-400 mt-1 flex-shrink-0" size={20} />
            <div>
              <h3 className="font-medium text-slate-700">Stripe Hosted Invoice</h3>
              <p className="text-sm text-slate-600 mt-1">
                Complete Stripe Connect to enable hosted invoice payment links.
              </p>
              {stripeInvoiceBlockedReason && (
                <p className="text-xs text-amber-700 mt-2">
                  {stripeInvoiceBlockedReason}
                </p>
              )}
              <span className="inline-block mt-2 text-xs text-slate-600 bg-slate-200 px-2 py-1 rounded">
                Unavailable
              </span>
            </div>
          </div>
        )}

        <button
          onClick={() => onSelect('checkout')}
          className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-blue-500
                     hover:bg-blue-50 text-left flex items-start gap-3 transition-colors"
        >
          <CreditCard className="text-blue-600 mt-1 flex-shrink-0" size={20} />
          <div>
            <h3 className="font-medium text-slate-900">Stripe Checkout</h3>
            <p className="text-sm text-slate-600 mt-1">
              Legacy one-time checkout page for direct card payment.
            </p>
            <span className="inline-block mt-2 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
              Legacy
            </span>
          </div>
        </button>

        {hasDefaultPaymentMethod && (
          <button
            onClick={() => onSelect('saved')}
            className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-green-500
                       hover:bg-green-50 text-left flex items-start gap-3 transition-colors"
          >
            <Zap className="text-green-600 mt-1 flex-shrink-0" size={20} />
            <div>
              <h3 className="font-medium text-slate-900">Charge Saved Card</h3>
              <p className="text-sm text-slate-600 mt-1">
                Automatically charge driver's default payment method.
              </p>
              <span className="inline-block mt-2 text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
                Instant
              </span>
            </div>
          </button>
        )}

        <button
          onClick={() => onSelect('manual')}
          className="w-full p-4 border-2 border-slate-200 rounded-lg hover:border-slate-500
                     hover:bg-slate-50 text-left flex items-start gap-3 transition-colors"
        >
          <FileText className="text-slate-600 mt-1 flex-shrink-0" size={20} />
          <div>
            <h3 className="font-medium text-slate-900">Manual Payment</h3>
            <p className="text-sm text-slate-600 mt-1">
              Send invoice email. Mark as paid manually after offline payment.
            </p>
            <span className="inline-block mt-2 text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
              {canUseStripeInvoice ? 'Offline' : 'Recommended (No Stripe Connect)'}
            </span>
          </div>
        </button>
      </div>

      <div className="border-t border-slate-200 px-6 py-4 bg-slate-50">
        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-900"
        >
          Cancel
        </button>
      </div>
    </Modal>
  );
};
